import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs/internal/Observable';
import { DotProgressIndicatorComponent } from 'src/app/shared/components/dot-progress-indicator/dot-progress-indicator.component';
import { IFileUploadObject } from 'src/app/shared/components/file-uploader/file-upload-object.interface';
import { SignaturePadComponent } from 'src/app/shared/components/signature-pad/signature-pad.component';
import { ButtonIconPositions } from 'src/app/shared/interfaces/button-icon-position.enum';
import { IDotProgressIndicatorStep } from 'src/app/shared/interfaces/dot-progress-indicator-step-interface';
import { IOptionItem } from 'src/app/shared/interfaces/option-item.interface';
import { FileExtensionPipe } from 'src/app/shared/pipes/file-extension/file-extension.pipe';
import { v4 as uuid } from 'uuid';
import { OrdersState } from '../../../shared/states/orders/orders.state';
import { FileType } from '../../interfaces/file-type.enum';
import { IFilesSignatureReceivedEvent } from '../../interfaces/files-signature-received-event.interface';
import { ReceiveAndSignAttachmentProgress } from '../../interfaces/receive-and-sign-attachment-progress.enum';

@Component({
  selector: 'app-receive-attachments-dialog',
  templateUrl: './receive-attachments-dialog.component.html',
  styleUrls: ['./receive-attachments-dialog.component.scss'],
})
export class ReceiveAttachmentsDialogComponent implements OnInit {
  @Input() show: boolean;
  @Input() editMode: boolean;
  @Input() requiresSignature: boolean;
  @Input() csosControl: FormControl;

  @Input() fileTypes: IOptionItem[];

  @Input() set uploadedFiles(files: any[]) {
    this.attachments = [...this.addFileDisplayNameToItems(files)];
    this.attachmentsOriginal = [...files];
  }

  @Output() closeDialog: EventEmitter<IFilesSignatureReceivedEvent> = new EventEmitter();
  @Output() cancel: EventEmitter<void> = new EventEmitter();

  FileType = FileType;

  subheader: string = '';

  attachments: IFileUploadObject[] = [];
  attachmentsOriginal: IFileUploadObject[] = [];
  isLoading: boolean;
  attachmentsChanged: boolean;
  invalidInputIndex = -1;
  invalidInputIndexes: number[] = [];
  ButtonIconPositions = ButtonIconPositions;
  ReceiveAndSignAttachmentProgress = ReceiveAndSignAttachmentProgress;

  steps: IDotProgressIndicatorStep[] = [
    {
      id: ReceiveAndSignAttachmentProgress.Attach,
      isActive: true
    },
    {
      id: ReceiveAndSignAttachmentProgress.Sign,
      canActivatePredicate: () => this.attachments.length && !this.anyInvalid() && !this.csosControl?.invalid
    }
  ];

  currentStep: IDotProgressIndicatorStep = this.steps[0];
  isSigned: boolean;
  signature?: string;

  firstStepSubheader = 'You can attach multiple files (PDF, PNG, JPEG)';

  @Select(OrdersState.loading) loading$: Observable<boolean | undefined>;

  @ViewChild('signature')
  public signaturePad: SignaturePadComponent;

  @ViewChild('stepper') 
  public stepper: DotProgressIndicatorComponent;

  form: FormGroup;

  constructor(protected store: Store, private fileExtensionPipe: FileExtensionPipe) {}

  ngOnInit(): void {
   if (!this.editMode) {
    this.subheader = this.firstStepSubheader;
   }
   this.addCSOSInputForDea222CSOS();
  }

  addAttachmentFile(file): void {
    file.id ??= uuid();
    this.attachments.push(file);
    this.attachmentsChanged = true;
  }

  afterFilesSelected(file: IFileUploadObject): void {
    if (file) {
      if (this.editMode) {
        this.attachments = [file];
      } else {
        this.attachments.push(file);
      }
   
      this.attachments = this.ensureUniqueNames(this.attachments);
      this.attachments = this.addFileDisplayNameToItems(this.attachments);
    }
    this.isLoading = true;
  }

  afterAllFilesUploaded(): void {
    this.isLoading = false;
  }

  deleteAttachment(i: number): void {
    this.attachments.splice(i, 1);
    this.attachmentsChanged = true;
    this.addCSOSInputForDea222CSOS();
  }

  saveAttachments(): void {
    this.closeDialog.emit(
      {
        attachments: this.ensureUniqueNames(this.attachments),
        signature: this.signature,
      }
    );
  }

  close(): void {
    this.invalidInputIndexes = [];
    this.cancel.emit();
  }

  fileTypeSelected(value: IOptionItem, index: number): void {
    this.attachments[index].type = value.id as FileType;
    this.addCSOSInputForDea222CSOS();
  }

  private addCSOSInputForDea222CSOS(): void {
    const firstDEA222 = this.attachments.find(x => x.type === FileType.DEA222CSOS);
    const deaFilesCount = this.attachments.filter(x => x.type === FileType.DEA222CSOS && x.shouldShowCSOS)?.length;
    if (firstDEA222 && deaFilesCount === 0) {
      this.attachments.forEach(x => x.shouldShowCSOS = false);
      firstDEA222.shouldShowCSOS = true;
      this.csosControl?.enable();
    } else if (!firstDEA222) {
      const attachment = this.attachments.find(x => x.shouldShowCSOS);
      if (attachment) {
        attachment.shouldShowCSOS = false;
      }
      this.csosControl?.disable();
    }
  }

  otherFileNameChanged(value: string, index: number): void {
    this.attachments[index].otherType = value;
  }

  anyInvalid(): boolean {
    return this.attachments?.some(x => !x.type || (x.type === FileType.Other && !x.otherType))
  }

  fileNameChange(event, index): void {
   let value = event.target.value;

    this.attachments[index].name = value + this.getFileExtension(index);

    if (this.editMode && this.attachments.filter(x => x.name === value).length > 0) {
      if(!this.invalidInputIndexes.includes(index))  
        this.invalidInputIndexes.push(index);
    }
    else if (!this.editMode && this.store.selectSnapshot(OrdersState.orderFiles).find(x => x.name === value)) {
      if(!this.invalidInputIndexes.includes(index))
        this.invalidInputIndexes.push(index);
    }
    else {
      if(this.invalidInputIndexes.includes(index))
      this.invalidInputIndexes = this.invalidInputIndexes.filter(x => x !== index);
    }
    
    if (this.attachments.length === this.attachmentsOriginal.length) {
      this.attachmentsChanged =
        this.attachmentsOriginal.some((el, i) => index !== i && el.name !== this.attachments[i]?.name) ||
        this.attachmentsOriginal[index].name !== value;
    }
  }

  private getFileExtension(index: number): string {
    const filename = this.attachments[index].name.trim();
    const extension = filename.split('.').pop();
    return '.' + extension;
  }

  private addFileDisplayNameToItems(files: any[]): any[] {
    return files?.map(obj => ({...obj, displayName: this.fileExtensionPipe.transform(obj?.name)}));
  }

  ensureUniqueNames(files: IFileUploadObject[]): IFileUploadObject[] {
    const existingFiles = this.store.selectSnapshot(OrdersState.orderFiles) || [];
    files.forEach(file => {
      file.id ??= uuid();
      file.createDate ??= new Date();

      let duplicate = existingFiles.find(x => x.name === file.name); //search the same file in already existing files 

      if (!duplicate && this.attachments.filter(x => x.name === file.name)?.length >= 2) { //search the same file in selected files
        const allWithName = this.attachments.filter(x => x.name === file.name); 
        duplicate = allWithName[1];
      }

      if (duplicate) {
        let counter = 1;
        const nameParts = file.name.split('.');
        const fileName = nameParts[0];
        const fileExtension = nameParts.pop();
        let newName = fileName + ` (${counter}).${fileExtension}`;
        while (existingFiles.find(x => x.name === newName) || this.attachments.find(x => x.name === newName)) {
          newName = fileName + ` (${counter++}).${fileExtension}`;
        }
        file.name = newName;
      }
    });
     return files.sort((x, y) => y.createDate.getTime() - x.createDate.getTime());
  }

  stepSelected(step: IDotProgressIndicatorStep): void {
    this.currentStep = step;

    if (step.id === ReceiveAndSignAttachmentProgress.Sign) {
      this.subheader = 'Digitally sign your file(s) for improved compliance.';
    }
    else {
      this.subheader = this.firstStepSubheader;
    }
  }

  selectSignStep(): void {
    if (this.csosControl?.invalid) {
      this.csosControl?.markAsTouched();
      this.csosControl?.markAsDirty();
      this.csosControl?.updateValueAndValidity();
      return;
    }
   this.stepper.selectStep(ReceiveAndSignAttachmentProgress.Sign);
  }

  async drawComplete(event: MouseEvent | Touch) {
    this.signature = this.signaturePad.toDataURL();
    this.isSigned = true;
  }

  signatureCleared(): void {
    this.isSigned = false;
   }

}
